import React, { useState, useEffect } from "react";
import { X, Save, Loader2 } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";

interface Props {
  user: any;
  onClose: () => void;
  onSuccess: () => void;
}

const EditTeamMemberModal: React.FC<Props> = ({ user, onClose, onSuccess }) => {
  const { role: myRole, token } = useAdminAuth();
  
  const [selectedRole, setSelectedRole] = useState(user.role);
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [selectedManager, setSelectedManager] = useState("");
  const [selectedTL, setSelectedTL] = useState("");
  
  const [admins, setAdmins] = useState([]);
  const [managers, setManagers] = useState([]);
  const [tls, setTls] = useState([]);
  const [loading, setLoading] = useState(false);

  const allowedRoles = myRole === "SUPER_ADMIN" 
    ? ["ADMIN", "MANAGER", "TEAM_LEADER", "AGENT"]
    : ["MANAGER", "TEAM_LEADER", "AGENT"];

  const fetchHierarchy = async (roleType: string, parentId: string | null, setState: any) => {
    let url = `http://localhost:2027/api/admin/team/hierarchy?role=${roleType}`;
    if (parentId) url += `&parentId=${parentId}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setState(await res.json());
  };

  useEffect(() => {
    if (myRole === "SUPER_ADMIN") fetchHierarchy("ADMIN", null, setAdmins);
  }, []);

  useEffect(() => {
    if (selectedAdmin || myRole === "ADMIN") {
        fetchHierarchy("MANAGER", selectedAdmin || null, setManagers);
    }
  }, [selectedAdmin]);

  useEffect(() => {
    if (selectedManager) fetchHierarchy("TEAM_LEADER", selectedManager, setTls);
  }, [selectedManager]);

  const handleUpdate = async () => {
    let finalParentId = null;

    // Logic: Role ke hisaab se parent select karo
    if (selectedRole === "ADMIN") {
        finalParentId = 1; // Direct Super Admin
    } else if (selectedRole === "MANAGER") {
        finalParentId = selectedAdmin ? Number(selectedAdmin) : null;
    } else if (selectedRole === "TEAM_LEADER") {
        finalParentId = selectedManager ? Number(selectedManager) : null;
    } else if (selectedRole === "AGENT") {
        finalParentId = selectedTL ? Number(selectedTL) : null;
    }

    setLoading(true);
    try {
        const res = await fetch("http://localhost:2027/api/admin/team/update-member", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ 
                userId: user.id, 
                newRole: selectedRole,
                newParentId: finalParentId // Yahan se dynamic ID jayegi
            })
        });

        if(res.ok) {
            onSuccess();
            onClose();
        } else {
            const errorMsg = await res.text();
            alert("Error: " + errorMsg);
        }
    } catch(e) { 
        console.error(e); 
    } finally { 
        setLoading(false); 
    }
};

  return (
    <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-[100] backdrop-blur-md p-4 font-sans">
      <div className="bg-white w-full max-w-lg rounded-[32px] border border-slate-100 p-10 shadow-[0_20px_60px_rgba(0,0,0,0.1)] relative">
        <button onClick={onClose} className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-50 rounded-full"><X size={20}/></button>
        
        <div className="mb-8">
            <h2 className="text-3xl font-bold text-[#0F172A] tracking-tight">Edit Member</h2>
            <p className="text-slate-500 text-sm mt-1">Modifying: <span className="text-emerald-600 font-extrabold">{user.name}</span></p>
        </div>

        <div className="space-y-6">
          <div>
            <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 ml-1">Team Role</label>
            <select 
              className="w-full mt-2 bg-slate-50 border border-slate-200 text-slate-700 p-4 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-600 outline-none transition-all shadow-sm"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              {allowedRoles.map(r => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
            </select>
          </div>

          <div className="p-6 bg-emerald-50/30 rounded-[24px] border border-emerald-100/50 space-y-4">
             <p className="text-[10px] text-emerald-700 uppercase font-extrabold tracking-widest flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                Reassign Reporting Structure
             </p>
             
             {myRole === "SUPER_ADMIN" && ["MANAGER", "TEAM_LEADER", "AGENT"].includes(selectedRole) && (
                <select className="w-full bg-white border border-slate-200 text-slate-600 p-3 rounded-xl text-sm outline-none focus:border-emerald-500" onChange={e => setSelectedAdmin(e.target.value)}>
                    <option value="">New Admin Parent</option>
                    {admins.map((a:any) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
             )}

             {["TEAM_LEADER", "AGENT"].includes(selectedRole) && (
                <select className="w-full bg-white border border-slate-200 text-slate-600 p-3 rounded-xl text-sm outline-none focus:border-emerald-500" onChange={e => setSelectedManager(e.target.value)}>
                    <option value="">New Manager Parent</option>
                    {managers.map((m:any) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
             )}

             {selectedRole === "AGENT" && (
                <select className="w-full bg-white border border-slate-200 text-slate-600 p-3 rounded-xl text-sm outline-none focus:border-emerald-500" onChange={e => setSelectedTL(e.target.value)}>
                    <option value="">New TL Parent</option>
                    {tls.map((t:any) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
             )}
          </div>
        </div>

        <div className="mt-10 flex items-center justify-between border-t border-slate-100 pt-8">
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xs uppercase tracking-widest font-extrabold transition-colors">Cancel</button>
            <button 
                onClick={handleUpdate}
                disabled={loading}
                className="bg-[#059669] hover:bg-[#047857] text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs flex items-center gap-3 shadow-lg shadow-emerald-100 transition-all active:scale-95 disabled:opacity-50"
            >
                {loading ? <Loader2 className="animate-spin" size={16}/> : "Save Update"} <Save size={18}/>
            </button>
        </div>
      </div>
    </div>
  );
};

export default EditTeamMemberModal;